/* eslint-disable max-len */
/* eslint-disable import-helpers/order-imports */
import React, {
  useState, useEffect, useContext, useCallback, useMemo, useRef,
} from 'react';
import {
  Card,
  Container,
  Row,
  Col,
  CardHeader,
  CardBody,
  Button,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { format } from 'date-fns';

import DatePickerMaskedInput from '../../components/DatePickerMaskedInput';
import GoToTop from '../../components/GoToTop';
import Notification from '../../components/Notification';
import Select from '../../components/Select';
import { AppContext } from '../../contexts/app';
import LayoutAdmin from '../../layouts/admin';
import api from '../../services/api';

const USERS_TODOS = [{ label: 'TODOS', value: null }];
const ESTADO_TODOS = { label: 'Todos', value: null };

const formatDatePayload = (date) => (date ? format(date, 'yyyy-MM-dd') : null);

export default function RelatorioUsuarios() {
  const notificationRef = useRef();
  const { setLoading } = useContext(AppContext);

  const [users, setUsers] = useState([]);
  const [usersRaw, setUsersRaw] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(USERS_TODOS);
  const [mandatoDe, setMandatoDe] = useState(null);
  const [mandatoAte, setMandatoAte] = useState(null);
  const [estado, setEstado] = useState(ESTADO_TODOS);
  const [ativo, setAtivo] = useState(-1);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data = [] } = await api.get('/users?size=0');

        setUsersRaw(data);
        setUsers(data.map((d) => ({
          label: `${d.name} - ${d.email} - ${d.cim} - ${d.uf_loja || d.uf || 'S/UF'}`,
          value: d.id,
        })));
      } catch (error) {
        console.error(error);
        notificationRef.current?.notify({
          message: 'Não foi possível carregar os filtros do relatório.',
          color: 'danger',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [setLoading]);

  const estadosOptions = useMemo(() => {
    const ufs = [...new Set(
      usersRaw
        .map((u) => (u.uf_loja || '').trim().toUpperCase())
        .filter(Boolean),
    )].sort();

    return [
      ESTADO_TODOS,
      ...ufs.map((uf) => ({ label: uf, value: uf })),
    ];
  }, [usersRaw]);

  const handleSelectedUser = useCallback((newSelectedUsers) => {
    setSelectedUsers(() => (!newSelectedUsers?.length
      ? USERS_TODOS
      : newSelectedUsers.filter((u) => u.value)));
  }, []);

  const buildPayload = useCallback(() => ({
    userIds: selectedUsers.filter((u) => u.value).map((u) => u.value),
    mandatoDe: formatDatePayload(mandatoDe),
    mandatoAte: formatDatePayload(mandatoAte),
    ufLoja: estado?.value || null,
    ativo,
  }), [selectedUsers, mandatoDe, mandatoAte, estado, ativo]);

  const handleGerarRelatorio = useCallback(async () => {
    try {
      setGenerating(true);
      setLoading(true);

      const { data } = await api.post('/relatorioUsuarios/excel', buildPayload(), {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'relatorio-usuarios.xlsx';
      link.click();
      URL.revokeObjectURL(url);

      notificationRef.current?.notify({
        message: 'Relatório gerado com sucesso.',
        color: 'success',
      });
    } catch (error) {
      console.error(error);
      notificationRef.current?.notify({
        message: 'Não foi possível gerar o relatório de usuários.',
        color: 'danger',
      });
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  }, [buildPayload, setLoading]);

  return (
    <LayoutAdmin>
      <Notification ref={notificationRef} />
      <Container>
        <GoToTop />
        <Row>
          <Col>
            <Card className="mt-5">
              <CardHeader>
                <h2>Relatório de Usuários</h2>
              </CardHeader>
            </Card>

            <Row className="mt-3">
              <Col>
                <Card>
                  <CardHeader>Filtros</CardHeader>
                  <CardBody>
                    <Row>
                      <Col xs={12} md={8}>
                        <Row className="mt-2">
                          <Col xs={12} md={2}>
                            <strong>Usuário:</strong>
                          </Col>
                          <Col xs={12} md={10}>
                            <Select
                              placeholder="Escolha um ou mais usuários"
                              value={selectedUsers}
                              options={users}
                              onChange={handleSelectedUser}
                              isMulti
                            />
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col xs={12} md={2}>
                            <strong>Período:</strong>
                          </Col>
                          <Col xs={12} md={10} tag="div" className="d-flex">
                            <DatePickerMaskedInput
                              placeholderText="Início de Mandato"
                              selected={mandatoDe}
                              open={false}
                              startOpen={false}
                              isClearable
                              onChange={(date) => setMandatoDe(date)}
                            />
                            <span className="ml-3 mr-3 mt-2">a</span>
                            <DatePickerMaskedInput
                              placeholderText="Fim de Mandato"
                              selected={mandatoAte}
                              open={false}
                              startOpen={false}
                              isClearable
                              onChange={(date) => setMandatoAte(date)}
                            />
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col xs={12} md={2}>
                            <strong>Estado:</strong>
                          </Col>
                          <Col xs={12} md={10}>
                            <Select
                              placeholder="Selecione o estado da loja"
                              value={estado}
                              options={estadosOptions}
                              onChange={(value) => setEstado(value || ESTADO_TODOS)}
                            />
                          </Col>
                        </Row>

                        <Row className="mt-3">
                          <Col xs={12} md={2}>
                            <strong>Status:</strong>
                          </Col>
                          <Col xs={12} md={10} tag="div" className="d-flex flex-wrap">
                            <FormGroup check className="mr-3">
                              <Input
                                type="radio"
                                name="statusUsuario"
                                checked={ativo === -1}
                                onChange={() => setAtivo(-1)}
                              />
                              <Label check>Todos</Label>
                            </FormGroup>
                            <FormGroup check className="mr-3">
                              <Input
                                type="radio"
                                name="statusUsuario"
                                checked={ativo === 1}
                                onChange={() => setAtivo(1)}
                              />
                              <Label check>Ativos</Label>
                            </FormGroup>
                            <FormGroup check className="mr-3">
                              <Input
                                type="radio"
                                name="statusUsuario"
                                checked={ativo === 0}
                                onChange={() => setAtivo(0)}
                              />
                              <Label check>Inativos</Label>
                            </FormGroup>
                          </Col>
                        </Row>
                      </Col>

                      <Col xs={12} md={4} className="mt-3 mt-md-0">
                        <Button
                          color="primary"
                          onClick={handleGerarRelatorio}
                          disabled={generating}
                          style={{
                            height: '75px',
                            fontWeight: 'bold',
                            borderRadius: '0.75rem',
                          }}
                          block
                        >
                          Gerar Relatório Excel
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </LayoutAdmin>
  );
}
